/**
 * Tarayıcının yerel medya ve donanım yeteneklerini dinamik olarak tespit eder.
 *
 * HEVC (H.265) ve HDR desteği işletim sistemine (Windows HEVC Video Extensions,
 * macOS/iOS Safari veya modern Chromium), GPU donanım hızlandırmasına ve monitöre
 * göre değişiklik gösterir.
 */
export interface ClientCapabilities {
  hevc: boolean;
  hdr: boolean;
  av1: boolean;
  ac3: boolean;
  eac3: boolean;
}

let cachedCapabilities: ClientCapabilities | null = null;

export function detectClientCapabilities(): ClientCapabilities {
  if (cachedCapabilities) {
    return cachedCapabilities;
  }

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    cachedCapabilities = {
      hevc: false,
      hdr: false,
      av1: false,
      ac3: false,
      eac3: false,
    };
    return cachedCapabilities;
  }

  // 1. Video öğesi ve MediaSource üzerinden HEVC kontrolü
  let hevcSupported = false;
  const testVideo = document.createElement('video');

  const hevcCodecs = [
    'video/mp4; codecs="hvc1.1.6.L93.B0"',
    'video/mp4; codecs="hev1.1.6.L93.B0"',
    'video/mp4; codecs="hvc1.1.6.L120.B0"', // Main 10 / HDR10
    'video/mp4; codecs="hev1.1.6.L120.B0"',
    'video/mp4; codecs="hvc1.2.4.L120.B0"',
  ];

  for (const codec of hevcCodecs) {
    if (
      (typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported(codec)) ||
      testVideo.canPlayType(codec) === 'probably'
    ) {
      hevcSupported = true;
      break;
    }
  }

  // 2. HDR (High Dynamic Range) Ekran / Video Kontrolü
  let hdrSupported = false;
  try {
    if (typeof window.matchMedia === 'function') {
      const dynamicRangeHigh = window.matchMedia('(dynamic-range: high)').matches;
      const videoDynamicRangeHigh = window.matchMedia('(video-dynamic-range: high)').matches;
      hdrSupported = dynamicRangeHigh || videoDynamicRangeHigh;
    }
  } catch {
    hdrSupported = false;
  }

  // 3. AV1 kontrolü
  let av1Supported = false;
  const av1Codecs = ['video/mp4; codecs="av01.0.08M.08"', 'video/webm; codecs="av01.0.08M.08"'];
  for (const codec of av1Codecs) {
    if (
      (typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported(codec)) ||
      testVideo.canPlayType(codec) === 'probably'
    ) {
      av1Supported = true;
      break;
    }
  }

  // 4. AC3 & E-AC3 (Dolby Digital / Plus) kontrolü
  const ac3Supported =
    testVideo.canPlayType('audio/mp4; codecs="ac-3"') === 'probably' ||
    testVideo.canPlayType('audio/mp4; codecs="ac-3"') === 'maybe';
  const eac3Supported =
    testVideo.canPlayType('audio/mp4; codecs="ec-3"') === 'probably' ||
    testVideo.canPlayType('audio/mp4; codecs="ec-3"') === 'maybe';

  cachedCapabilities = {
    hevc: hevcSupported,
    hdr: hdrSupported,
    av1: av1Supported,
    ac3: ac3Supported,
    eac3: eac3Supported,
  };

  return cachedCapabilities;
}
